import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import PackageForm from './PackageForm';
import PackageCard from './PackageCard';

const PackageManagement = ({ setMessage }) => {
    const { getPackages } = useAdmin();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPackageForm, setShowPackageForm] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);

    useEffect(() => {
        loadPackages();
    }, []);

    const loadPackages = async () => {
        try {
            setLoading(true);
            const packagesData = await getPackages();
            setPackages(packagesData);
        } catch (error) {
            setMessage('Error loading packages: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditPackage = (pkg) => {
        setEditingPackage(pkg);
        setShowPackageForm(true);
    };

    const handlePackageSaved = () => {
        setShowPackageForm(false);
        setEditingPackage(null);
        loadPackages(); // Reload packages after save
    };

    const handleFormClose = () => {
        setShowPackageForm(false);
        setEditingPackage(null);
    };

    const handlePackageDeleted = () => {
        loadPackages(); // Reload packages after delete
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1 fw-bold">Package Management</h2>
                    <p className="text-muted mb-0">Create and manage travel packages</p>
                </div>

                <div className="d-flex align-items-center gap-3">
                    <span className="badge bg-primary fs-6">
                        <i className="fas fa-box me-1"></i>
                        {packages.length} {packages.length === 1 ? 'Package' : 'Packages'}
                    </span>
                    <button
                        onClick={() => setShowPackageForm(true)}
                        disabled={loading}
                        className="btn btn-primary"
                    >
                        <i className="fas fa-plus me-2"></i>
                        Add New Package
                    </button>
                </div>
            </div>

            {/* Package Form Modal */}
            {showPackageForm && (
                <PackageForm
                    package={editingPackage}
                    onSave={handlePackageSaved}
                    onClose={handleFormClose}
                    setMessage={setMessage}
                />
            )}

            {/* Packages Grid */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading packages...</p>
                </div>
            ) : packages.length > 0 ? (
                <div className="row">
                    {packages.map(pkg => (
                        <div key={pkg._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                            <PackageCard
                                package={pkg}
                                onEdit={handleEditPackage}
                                onDelete={handlePackageDeleted}
                                setMessage={setMessage}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-5">
                    <div className="mb-4">
                        <i className="fas fa-box-open fa-4x text-muted"></i>
                    </div>
                    <h4 className="text-muted">No packages yet</h4>
                    <p className="text-muted mb-4">Get started by creating your first travel package</p>
                    <button
                        onClick={() => setShowPackageForm(true)}
                        className="btn btn-primary btn-lg"
                    >
                        <i className="fas fa-plus me-2"></i>
                        Create Your First Package
                    </button>
                </div>
            )}
        </div>
    );
};

export default PackageManagement;